'use client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { User } from '@/api/auth';
import { useGetCommonStore } from '@/store';
import { assignABTestVersion as assignABTestVersionFunction } from '../helpers/assignABTestVersion';
import {
  checkAbTestVersion,
  checkUserGroupsABVersion,
} from '../helpers/checkAbTestVersion';
import { getCmsScaledVersion } from '../helpers/getCmsScaledVersion';
import { isUserAppropriateForTest } from '../helpers/isUserAppropriateForTest';
import type { ABTestVersion } from '../types';

interface useABTestingV2Params {
  experiment: string;
  versions?: ABTestVersion[];
}

export const useABTestingV2 = ({
  experiment,
  versions,
}: useABTestingV2Params) => {
  const useCommonStore = useGetCommonStore();

  const user = useCommonStore.use.user();
  const refreshUser = useCommonStore.use.refreshUser();
  const cmsABTests = useCommonStore.use.cmsABTests();
  const [version, setVersion] = useState<string | undefined>();

  const normalizedVersions = useMemo(
    () => versions ?? createDefaultVersions(experiment),
    [versions, experiment]
  );

  const abTestParams = useMemo(
    () => ({
      user,
      experiment,
      cmsABTests,
      refreshUser,
      versions: normalizedVersions,
    }),
    [user, experiment, cmsABTests, refreshUser, normalizedVersions]
  );

  useEffect(() => {
    setVersion(checkAbTestVersion(abTestParams));
  }, [abTestParams]);

  const resolvedVersion = useMemo(() => {
    if (!version) return;

    const versionsNames = normalizedVersions.map(({ name }) => name);
    const cmsVersion = getCmsScaledVersion(
      experiment,
      versionsNames,
      cmsABTests
    );
    const groupVersion = checkUserGroupsABVersion(versionsNames, user);

    // CMS rollout and server-assigned groups are authoritative — no date gate
    if (version === cmsVersion || version === groupVersion) return version;

    // LS-sourced version — apply date gate
    return isUserAppropriateForTest(experiment, user?.createdAt)
      ? version
      : undefined;
  }, [experiment, user, cmsABTests, normalizedVersions, version]);

  const assignABTestVersion = useCallback(
    (targetUser?: User) => {
      const params = targetUser
        ? { ...abTestParams, user: targetUser }
        : abTestParams;

      const versionsNames = normalizedVersions.map(({ name }) => name);

      // CMS rollout — authoritative, no date gate
      const cmsVersion = getCmsScaledVersion(
        experiment,
        versionsNames,
        params.cmsABTests
      );
      if (cmsVersion) {
        setVersion(cmsVersion);
        return formatVersion(normalizedVersions, cmsVersion);
      }

      // Server-assigned group — authoritative, no date gate
      const groupVersion = checkUserGroupsABVersion(versionsNames, params.user);
      if (groupVersion) {
        setVersion(groupVersion);
        return formatVersion(normalizedVersions, groupVersion);
      }

      // LS and new assignments — date gate applies
      if (!isUserAppropriateForTest(experiment, params.user?.createdAt)) {
        return formatVersion(normalizedVersions, undefined);
      }

      const existingVersion = checkAbTestVersion(params);
      if (existingVersion) {
        setVersion(existingVersion);
        return formatVersion(normalizedVersions, existingVersion);
      }

      const newVersion = assignABTestVersionFunction(params);
      setVersion(newVersion);
      return formatVersion(normalizedVersions, newVersion);
    },
    [abTestParams, experiment, normalizedVersions]
  );

  return useMemo(
    () => ({
      ...formatVersion(normalizedVersions, resolvedVersion),
      assignABTestVersion,
    }),
    [assignABTestVersion, normalizedVersions, resolvedVersion]
  );
};

const formatVersion = (versions: ABTestVersion[], version?: string) => ({
  version,
  isA: version === versions[0].name,
  isB: version === versions[1].name,
});

const createDefaultVersions = (experiment: string): ABTestVersion[] => [
  { name: `${experiment}:a`, probability: 50 },
  { name: `${experiment}:b`, probability: 50 },
];
